import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';

const RegionalInsights = () => {
  const [selectedRegion, setSelectedRegion] = useState('north-america');

  const regionalData = {
    'north-america': {
      name: 'North America',
      totalStartups: 1247,
      activePrograms: 89,
      avgConversion: 42.3,
      topSectors: ['FinTech', 'HealthTech', 'AI/ML', 'CleanTech'],
      growth: '+15.2%',
      cities: [
        { name: 'San Francisco', startups: 342, matches: 89, conversion: 45.2 },
        { name: 'New York', startups: 298, matches: 76, conversion: 41.8 },
        { name: 'Boston', startups: 187, matches: 52, conversion: 38.9 },
        { name: 'Austin', startups: 156, matches: 41, conversion: 36.7 },
        { name: 'Toronto', startups: 134, matches: 35, conversion: 34.2 }
      ]
    },
    'europe': {
      name: 'Europe',
      totalStartups: 892,
      activePrograms: 67,
      avgConversion: 38.7,
      topSectors: ['GreenTech', 'FinTech', 'MedTech', 'EdTech'],
      growth: '+12.8%',
      cities: [
        { name: 'London', startups: 245, matches: 68, conversion: 42.1 },
        { name: 'Berlin', startups: 189, matches: 51, conversion: 39.3 },
        { name: 'Paris', startups: 167, matches: 45, conversion: 37.8 },
        { name: 'Amsterdam', startups: 142, matches: 38, conversion: 35.9 },
        { name: 'Stockholm', startups: 98, matches: 26, conversion: 33.4 }
      ]
    },
    'asia-pacific': {
      name: 'Asia Pacific',
      totalStartups: 1089,
      activePrograms: 78,
      avgConversion: 35.9,
      topSectors: ['E-commerce', 'FinTech', 'Gaming', 'IoT'],
      growth: '+22.4%',
      cities: [
        { name: 'Singapore', startups: 287, matches: 72, conversion: 38.2 },
        { name: 'Sydney', startups: 234, matches: 59, conversion: 36.7 },
        { name: 'Tokyo', startups: 198, matches: 48, conversion: 34.1 },
        { name: 'Seoul', startups: 176, matches: 42, conversion: 32.8 },
        { name: 'Mumbai', startups: 194, matches: 45, conversion: 31.5 }
      ]
    }
  };

  const regions = [
    { id: 'north-america', name: 'North America', icon: 'MapPin' },
    { id: 'europe', name: 'Europe', icon: 'Globe' },
    { id: 'asia-pacific', name: 'Asia Pacific', icon: 'Compass' }
  ];

  const currentData = regionalData?.[selectedRegion];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Regional Market Insights
          </h3>
          <p className="text-sm text-muted-foreground">
            Analyze startup ecosystem performance by geographic region
          </p>
        </div>

        <div className="flex items-center space-x-1 bg-muted rounded-lg p-1 mt-4 sm:mt-0">
          {regions?.map((region) => (
            <button
              key={region?.id}
              onClick={() => setSelectedRegion(region?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                selectedRegion === region?.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={region?.icon} size={16} />
              <span className="hidden sm:inline">{region?.name}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Regional Overview */}
        <div className="lg:col-span-1">
          <div className="bg-muted/50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-foreground mb-3">{currentData?.name} Overview</h4>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Startups</span>
                <span className="font-semibold text-foreground">{currentData?.totalStartups?.toLocaleString()}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Active Programs</span>
                <span className="font-semibold text-foreground">{currentData?.activePrograms}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Avg Conversion</span>
                <span className="font-semibold text-foreground">{currentData?.avgConversion}%</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Growth Rate</span>
                <span className="font-semibold text-success">{currentData?.growth}</span>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <h5 className="font-medium text-foreground mb-3">Top Sectors</h5>
            <div className="space-y-2">
              {currentData?.topSectors?.map((sector, index) => (
                <div key={sector} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span className="text-sm text-foreground">{sector}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* City Performance */}
        <div className="lg:col-span-2">
          <h4 className="font-semibold text-foreground mb-4">Top Performing Cities</h4>
          
          <div className="space-y-3">
            {currentData?.cities?.map((city, index) => (
              <div key={city?.name} className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">{index + 1}</span>
                    </div>
                    <div>
                      <h5 className="font-medium text-foreground">{city?.name}</h5>
                      <p className="text-xs text-muted-foreground">{city?.startups} startups</p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">{city?.matches} matches</p>
                    <p className="text-xs text-muted-foreground">{city?.conversion}% conversion</p>
                  </div>
                </div>
                
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(city?.conversion / 50) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="TrendingUp" size={16} />
            <span>Data updated 2 hours ago</span>
          </div>
          
          <Button variant="outline" size="sm" className="mt-3 sm:mt-0">
            <Icon name="Download" size={16} className="mr-2" />
            Export Regional Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegionalInsights;